// Brand fonts (self-hosted; see styles/globals.css @theme).
import "@fontsource-variable/inter";
import "@fontsource-variable/geologica";
import { Trans } from "@lingui/react/macro";
import { cn } from "@pontimed/ui";
import { captureRootError } from "@/lib/sentry";
import { useRefreshToken } from "@pontimed/auth-client";
import { BfcacheGuard } from "@pontimed/ui/lib/bfcache-guard";
import { HapticProvider } from "@pontimed/ui/lib/haptic-provider";
import { VisualViewportBottomProbe } from "@pontimed/ui/lib/use-visual-viewport-bottom";
import { useScrollLockSafetyNet } from "@pontimed/ui/sheet-kit";
import type { QueryClient } from "@tanstack/react-query";
import {
  Link,
  Outlet,
  createRootRouteWithContext,
  useCanGoBack,
  useRouterState,
} from "@tanstack/react-router";
import { Search } from "lucide-react";

import { AuthModalProvider } from "@/components/auth/auth-modal-context";
import { CountryGate } from "@/components/country/country-gate";
import { Button } from "@/components/ui/button";
import { buttonVariants } from "@/components/ui/button-variants";
import { FeedbackProvider } from "@/components/feedback/feedback-context";
import { CookieConsentBanner } from "@/components/layout/cookie-consent-banner";
import { HeroBlob } from "@/components/layout/hero-blob";
import { PageHeader } from "@/components/layout/page-header";
import { NavigationProgress } from "@/components/layout/navigation-progress";
import { LegalSheetProvider } from "@/components/legal/legal-sheet-provider";
import { UpdateBanner } from "@/components/layout/update-banner";
import { PostHogIdentityBridge } from "@/lib/analytics/identity-bridge";
import { PostHogPageviewTracker } from "@/lib/analytics/pageview-tracker";
import { ReferralShareProvider } from "@/components/referral/referral-share-context";
import { ReferralShareSheet } from "@/components/referral/referral-share-sheet";

/**
 * `RouterContext` is the typed shape injected via `createRouter({ context })`
 * in main.tsx. Route `loader`s receive it as `({ context }) => …` and call
 * `context.queryClient.ensureQueryData(...)` to prefetch in parallel with
 * the route chunk download, so component mount sees a hot cache.
 */
export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: RootError,
});

function RootComponent() {
  useRefreshToken();
  // Recover from any orphaned `position: fixed` on body left over from a
  // sheet/dialog that didn't unmount cleanly. Tied to pathname so it re-runs
  // on every route change. See `@pontimed/ui/sheet-kit`.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useScrollLockSafetyNet(pathname);
  // Clinic sites frame /widget/* and kiosk screens show /display/*: no cookie
  // banner or update prompt there. In a third-party iframe the consent cookie
  // can't even be stored, so the banner came back on every load. (PostHog
  // stays: it only tracks after consent.)
  const isEmbedded = pathname.startsWith("/widget/") || pathname.startsWith("/display");
  return (
    <div className="text-foreground relative isolate min-h-dvh">
      <NavigationProgress />
      <HeroBlob />
      <BfcacheGuard />
      <HapticProvider />
      <VisualViewportBottomProbe />
      {/* Analytics — both rely on TanStack Router context (identity bridge
          uses `useSession` from auth-client which queries via React Query;
          pageview tracker subscribes to `useRouterState`). They render null,
          so position inside the layout doesn't matter. */}
      <PostHogIdentityBridge />
      <PostHogPageviewTracker />
      <LegalSheetProvider>
        <AuthModalProvider>
          <ReferralShareProvider>
            <FeedbackProvider>
              <CountryGate>
                <Outlet />
              </CountryGate>
              {!isEmbedded && <UpdateBanner />}
              <ReferralShareSheet />
              {!isEmbedded && <CookieConsentBanner />}
            </FeedbackProvider>
          </ReferralShareProvider>
        </AuthModalProvider>
      </LegalSheetProvider>
    </div>
  );
}

function NotFound() {
  // P16: never a dead end. Opened from an email, a push or a native cold
  // start there is no history, so "Back" only shows when it can work; the
  // way out is always search.
  const canGoBack = useCanGoBack();
  return (
    <div className="bg-background text-foreground flex min-h-dvh items-center justify-center px-6">
      <div className="w-full max-w-md">
        <PageHeader
          title={<Trans>Page not found</Trans>}
          description={<Trans>The page you are looking for does not exist or has moved.</Trans>}
        />
        <div className="mt-6 flex flex-wrap gap-2">
          <Link to="/find" className={cn(buttonVariants({ size: "touch" }))}>
            <Search className="size-4" aria-hidden />
            <Trans>Find a doctor</Trans>
          </Link>
          {canGoBack && (
            <Button variant="outline" size="touch" onClick={() => history.back()}>
              <Trans>Go back</Trans>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function RootError({ error }: { error: Error }) {
  // F6: surface route-level crashes to Sentry — this boundary was silent.
  captureRootError(error);
  // P16: a translated sentence for people; the raw message (English,
  // technical) only behind a collapsed "Details" for support.
  return (
    <div className="bg-background text-foreground flex min-h-dvh items-center justify-center px-6">
      <div className="w-full max-w-md">
        <PageHeader
          title={<Trans>Something went wrong</Trans>}
          description={
            <Trans>Reload the page to try again. If it keeps happening, go to search.</Trans>
          }
        />
        <div className="mt-6 flex flex-wrap gap-2">
          <Button size="touch" onClick={() => location.reload()}>
            <Trans>Reload</Trans>
          </Button>
          <Link to="/find" className={cn(buttonVariants({ variant: "outline", size: "touch" }))}>
            <Trans>Go to search</Trans>
          </Link>
        </div>
        {error.message && (
          <details className="text-muted-foreground mt-6 text-xs">
            <summary className="inline-flex min-h-11 cursor-pointer items-center">
              <Trans>Details</Trans>
            </summary>
            <p className="bg-muted mt-1 rounded-lg p-3 font-mono break-words">{error.message}</p>
          </details>
        )}
      </div>
    </div>
  );
}
